import { Link } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Database, RefreshCw } from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { usePlanConfig, usePlanRules } from "@/lib/api/plan-lab";
import { cn } from "@/lib/utils";

import { type PlanColumn, PlanTable } from "../kit/plan-table";
import {
	GROUP_TONE,
	type NodeLinkKey,
	PLAN_OBJECTS,
	type PlanObject,
	RUNTIME_EXTRAS,
	objectsOfStep,
	planGroup,
	planPage,
} from "../page-registry";
import {
	BlockTitle,
	Legend,
	NodeLink,
	PageLink,
	STAGES,
	type Stage,
	StageCard,
	type StageKey,
	StageNo,
	circled,
	stage,
} from "./shared";

/**
 * 运行时流程（一轮从客户原话到回复），从上到下一列拉通：
 * 客户原话 ↓ ① ↓ ② ↓ ③ ↓【逐件推进：④ ↓↑ ⑤，左侧回环】↓ ⑥ ↓ ⑦ ↓ 回复客户，下面是事实表横带，最底部是可折叠的「对象 × 七步」对照表。
 * 每一行左边是环节卡、右边同一行是这个环节的固定内容：做什么、产出、不管、关键机制名、用到的对象、规则条数（文字取注册表，条数取规则接口，不手写小点）。
 * 整张环节卡可点、跳到它归属的页面；步骤名和页面名相同时不再重复标页面，不同时卡上加一行小字「页面：xxx」。
 * `focus`：从页面地图详情区点 ①～⑦ 过来时高亮那一步并滚到视野中。
 */

/** 三列：回环槽、卡片、说明 */
const GRID = "grid grid-cols-[2.75rem_11rem_minmax(0,1fr)] items-center gap-x-0";
/** 框外的行补上与逐件推进框（2px 边 ＋ px-3）相同的左右留白，保证卡片对齐 */
const OUTSIDE = "border-x-2 border-transparent px-3";

/** 步数、时间、无进展上限：读当前生效配置 plan.budget.*，不手写；没取到显示「—」 */
function useBudget() {
	const c = usePlanConfig().data?.content;
	const n = (k: string): string => (typeof c?.[k] === "number" ? String(c[k]) : "—");
	return {
		item: n("plan.budget.item_steps"),
		turn: n("plan.budget.turn_steps"),
		wall: n("plan.budget.wall_clock_s"),
		noProgress: n("plan.budget.no_progress"),
	};
}

/** 某个对象在这一步用到：按维护页所在一级着色，悬停看它在这一步管什么；Skill 还在方案阶段，虚线 */
function ObjectChip({ o, step }: { o: PlanObject; step: StageKey }) {
	const tip = `${o.label}：${o.steps[step] ?? ""}`;
	if (!o.page || o.planned) {
		return (
			<span
				className="inline-flex items-center gap-1 rounded-full border border-dashed border-surface-border px-2 py-0.5 text-xs text-text-muted"
				title={`${tip}（方案阶段）`}
				data-testid={`plan-overview-obj-${step}-${o.key}`}
			>
				{o.label}
				<span className="text-text-disabled">方案阶段</span>
			</span>
		);
	}
	const t = GROUP_TONE[planGroup(planPage(o.page).group).tone];
	return (
		<PageLink
			id={`obj-${step}-${o.key}`}
			page={o.page}
			className={cn(
				"inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
				t.chip,
			)}
		>
			<span title={tip}>{o.label}</span>
		</PageLink>
	);
}

/** 「规则 N 条 →」：跳运行规则页并按环节筛选；N 取规则接口，没取到不显示数字 */
function RuleLink({
	ruleStage,
	n,
	label,
}: { ruleStage: string; n: number | null; label?: string }) {
	return (
		<Link
			to="/plan-lab"
			search={{ stage: ruleStage }}
			hash="guards"
			className="inline-flex items-center rounded-full text-xs font-medium text-brand-accent hover:underline"
			title={`打开运行规则页，只看「${ruleStage}」环节`}
			data-testid={`plan-overview-rules-${ruleStage}`}
		>
			{label ?? "规则"} {n === null ? "" : `${n} 条 `}→
		</Link>
	);
}

/** 各环节的规则条数（接口 `GET /rules`，按规则的「环节」字段数） */
function useRuleCounts(): (stage: string) => number | null {
	const rules = usePlanRules().data?.rules;
	return (st) => (rules ? rules.filter((r) => r.stage === st).length : null);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<span className="inline-flex gap-1.5">
			<span className="shrink-0 text-text-muted">{label}</span>
			<span>{children}</span>
		</span>
	);
}

function Note({ s }: { s: Stage }) {
	const count = useRuleCounts();
	const budget = useBudget();
	const objs = objectsOfStep(s.key);
	return (
		<div className="space-y-1.5 pl-6" data-testid={`plan-overview-note-${s.key}`}>
			<p className="text-sm text-text-primary">{s.what}</p>
			<div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-text-secondary">
				<Field label="产出">{s.out}</Field>
				<Field label="不管">{s.not}</Field>
			</div>
			{s.mechs.length || s.key === "settle" ? (
				<div className="flex flex-wrap items-center gap-1.5 text-xs text-text-secondary">
					<span className="text-text-muted">关键机制</span>
					{s.mechs.map((m) => (
						<Badge key={m} tone="muted">
							{m}
						</Badge>
					))}
					{s.key === "settle" ? (
						<span data-testid="plan-overview-budget-settle">
							连续 {budget.noProgress} 轮没进展转人工
						</span>
					) : null}
				</div>
			) : null}
			<div className="flex flex-wrap items-center gap-1.5">
				<span className="text-xs text-text-muted">用到的对象</span>
				{objs.map((o) => (
					<ObjectChip key={o.key} o={o} step={s.key} />
				))}
				<span className="ml-auto">
					<RuleLink ruleStage={s.ruleStage} n={count(s.ruleStage)} />
				</span>
			</div>
		</div>
	);
}

/** 一个环节一行：左卡右文 */
function StepRow({
	k,
	inside = false,
	focus,
}: { k: StageKey; inside?: boolean; focus: StageKey | null }) {
	const s = stage(k);
	const on = focus === k;
	const ref = React.useRef<HTMLSpanElement>(null);
	React.useEffect(() => {
		if (on) ref.current?.scrollIntoView({ block: "center" });
	}, [on]);
	return (
		<div className={inside ? GRID : `${GRID} ${OUTSIDE}`}>
			<span />
			{/* 白底垫一层，放进琥珀框里大模型卡的浅青底不发灰；高亮只用 ring，不改尺寸 */}
			<span
				ref={ref}
				className={cn("rounded-lg bg-surface-card", on && "ring-2 ring-brand-accent ring-offset-2")}
				data-testid={`plan-overview-step-${k}`}
				data-focus={on ? "1" : "0"}
			>
				<StageCard s={s} id={`rt-${k}`} className="h-28 w-44" />
			</span>
			<Note s={s} />
		</div>
	);
}

/** 行与行之间的向下箭头（落在卡片列正中） */
function Down({ inside = false, children }: { inside?: boolean; children?: React.ReactNode }) {
	return (
		<div className={inside ? GRID : `${GRID} ${OUTSIDE}`} aria-hidden>
			<span />
			<span className="flex h-7 items-center justify-center gap-3 text-xs text-text-muted">
				{children ?? <ArrowDown className="h-4 w-4" />}
			</span>
			<span />
		</div>
	);
}

function Io({ id, to, children }: { id: string; to: NodeLinkKey; children: React.ReactNode }) {
	return (
		<div className={`${GRID} ${OUTSIDE}`}>
			<span />
			<span className="flex w-44 justify-center">
				<NodeLink
					id={id}
					to={to}
					look="border-text-muted bg-surface-subtle"
					className="inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium"
				>
					{children}
				</NodeLink>
			</span>
			<span />
		</div>
	);
}

/** 逐件推进：琥珀色实线框包住 ④ ⑤，左侧回环从 ⑤ 绕回 ④ */
function AdvanceLoop({ focus }: { focus: StageKey | null }) {
	const budget = useBudget();
	return (
		<div
			className="rounded-xl border-2 border-brand-warning bg-brand-warning/10 px-3 pb-3 pt-2.5"
			data-testid="plan-overview-advance"
		>
			<div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
				<span className="inline-flex items-center gap-1.5 font-semibold text-text-primary">
					<RefreshCw className="h-4 w-4 text-brand-warning" aria-hidden />
					逐件推进
				</span>
				<span className="text-xs text-text-secondary">
					每件事项反复：决策 → 把关执行，直到办完、要问客户或办不了
				</span>
				<span
					className="text-xs text-text-secondary"
					title="来自当前生效配置 plan.budget.*"
					data-testid="plan-overview-budget"
				>
					每件每轮最多 {budget.item} 步 · 整轮最多 {budget.turn} 步 · {budget.wall} 秒
				</span>
			</div>
			<div className="relative">
				{/* 回环：从 ⑤ 左侧出发，沿左槽向上，箭头指回 ④ */}
				<div
					className="pointer-events-none absolute bottom-12 left-3.5 top-12 w-[1.875rem] rounded-l-xl border-y-[3px] border-l-[3px] border-brand-warning"
					aria-hidden
				>
					<ChevronRight
						className="absolute -right-2.5 -top-[0.75rem] h-6 w-6 text-brand-warning"
						strokeWidth={3}
					/>
					<span className="absolute -left-[0.875rem] top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-brand-warning">
						<RefreshCw className="h-3.5 w-3.5 text-text-inverse" strokeWidth={2.5} />
					</span>
				</div>
				<StepRow k="decide" inside focus={focus} />
				<Down inside>
					<span className="inline-flex items-center gap-0.5 text-brand-warning">
						<ArrowDown className="h-4 w-4" />
						下一步
					</span>
					<span className="inline-flex items-center gap-0.5 text-brand-warning">
						<ArrowUp className="h-4 w-4" />
						结果
					</span>
				</Down>
				<StepRow k="execute" inside focus={focus} />
			</div>
		</div>
	);
}

function FactBand() {
	const users: StageKey[] = ["signal", "decide", "execute", "reply"];
	const count = useRuleCounts();
	return (
		<div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 rounded-lg border border-surface-border bg-surface-subtle px-4 py-2.5 text-center text-sm">
			<NodeLink
				id="rt-facts"
				to="facts"
				look="border-transparent bg-transparent"
				className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 rounded-lg px-1 text-center text-sm"
			>
				<span className="inline-flex items-center gap-1.5 font-semibold">
					<Database className="h-4 w-4 text-text-secondary" aria-hidden />
					事实表 · 贯穿全程
				</span>
				<span className="text-text-secondary">查到的业务数据只存一份，各环节按需取用</span>
				<span className="inline-flex items-center gap-1 text-xs text-text-muted">
					用到它的：
					{users.map((k) => (
						<StageNo key={k} s={stage(k)} className="h-4 w-4" />
					))}
				</span>
			</NodeLink>
			{RUNTIME_EXTRAS.factStages.map((st) => (
				<RuleLink key={st} ruleStage={st} n={count(st)} label={`${st}类规则`} />
			))}
		</div>
	);
}

// ── 底部对照表：行＝承载对象，列＝七步（基线 03 矩阵，取注册表 objects），默认收起 ──

const COLUMNS: PlanColumn<PlanObject>[] = [
	{
		key: "name",
		title: "对象",
		className: "w-28",
		render: (o) =>
			o.page && !o.planned ? (
				<PageLink
					id={`matrix-row-${o.key}`}
					page={o.page}
					className="inline-flex rounded-md px-2 py-0.5 text-sm font-medium"
				>
					{o.label}
				</PageLink>
			) : (
				<span
					className="inline-flex items-center gap-1 rounded-md border border-dashed border-surface-border px-2 py-0.5 text-sm text-text-muted"
					data-testid={`plan-overview-node-matrix-row-${o.key}`}
				>
					{o.label}
					<span className="text-xs">方案阶段</span>
				</span>
			),
	},
	...STAGES.map(
		(s): PlanColumn<PlanObject> => ({
			key: s.key,
			title: `${circled(s.no)} ${s.name}`,
			className: "min-w-[8.5rem] whitespace-normal py-2 text-xs text-text-secondary",
			render: (o) => o.steps[s.key] ?? <span className="text-text-disabled">—</span>,
		}),
	),
];

function StepMatrix() {
	const [open, setOpen] = React.useState(false);
	return (
		<div className="space-y-2 border-t border-surface-border pt-4">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				aria-expanded={open}
				className="inline-flex items-center gap-1 rounded-full text-sm font-semibold text-text-primary hover:text-brand-accent"
				data-testid="plan-overview-matrix-toggle"
			>
				{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
				对象 × 七步
				<span className="text-xs font-normal text-text-secondary">
					（每类对象在哪一步管什么；页面读／管见页面地图详情）
				</span>
			</button>
			{open ? (
				<div data-testid="plan-overview-matrix">
					<PlanTable columns={COLUMNS} rows={PLAN_OBJECTS} rowKey={(o) => o.key} />
				</div>
			) : null}
		</div>
	);
}

export function RuntimeFlow({ focus = null }: { focus?: StageKey | null }) {
	return (
		<Card data-testid="plan-block-runtime">
			<CardContent className="space-y-5 p-6">
				<BlockTitle
					title="运行时流程"
					sub="一轮怎么跑：客户说一句话，系统按这七步给出回复；点卡片打开这一步所在的页面，规则细节点「规则 N 条」"
					right={<Legend />}
				/>
				<div className="overflow-x-auto">
					<div className="min-w-[44rem]" data-testid="plan-overview-mainflow">
						<Io id="rt-pin" to="snapshot">
							{RUNTIME_EXTRAS.pin.name}
						</Io>
						<Down />
						<Io id="rt-input" to="ask">
							客户原话
						</Io>
						<Down />
						<StepRow k="signal" focus={focus} />
						<Down />
						<StepRow k="board" focus={focus} />
						<Down />
						<StepRow k="recall" focus={focus} />
						<Down />
						<AdvanceLoop focus={focus} />
						<Down />
						<StepRow k="settle" focus={focus} />
						<Down />
						<StepRow k="reply" focus={focus} />
						<Down />
						<Io id="rt-output" to="history">
							回复客户
						</Io>
					</div>
				</div>
				<FactBand />
				<StepMatrix />
			</CardContent>
		</Card>
	);
}
